<template>
    <div
        :class="{'agile--ssr': isSSR, 'agile--auto-play': settings.autoplay, 'agile--disabled': settings.unagile, 'agile--fade': settings.fade && !settings.unagile, 'agile--rtl': settings.rtl, 'agile--no-nav-buttons': !settings.navButtons}"
        class="agile"
        @touchstart="() => {}"
    >
        <div
            ref="list"
            class="agile__list"
        >
            <div
                ref="track"
                :style="{transform: `translate(${translateX + marginX}px)`, transition: `transform ${settings.timing} ${transitionDelay}ms`}"
                class="agile__track"
                @mouseout="handleMouseOut('track')"
                @mouseover="handleMouseOver('track')"
            >
                <div
                    v-show="slidesCloned"
                    ref="slidesClonedBefore"
                    class="agile__slides agile__slides--cloned"
                >
                    <slot/>
                </div>

                <div
                    ref="slides"
                    class="agile__slides agile__slides--regular"
                >
                    <slot/>
                </div>

                <div
                    v-show="slidesCloned"
                    ref="slidesClonedAfter"
                    class="agile__slides agile__slides--cloned"
                >
                    <slot/>
                </div>
            </div>
        </div>

        <div
            v-if="$slots.caption"
            class="agile__caption"
        >
            <slot name="caption"/>
        </div>


        <button
            v-if="settings.navButtons && !settings.unagile"
            ref="prevButton"
            :disabled="!canGoToPrev"
            aria-label="Previous"
            class="agile__nav-button agile__nav-button--prev"
            type="button"
            @click="goToPrev(), restartAutoPlay()"
        >
            <slot name="prevbutton">
                ←
            </slot>
        </button>

        <ul
            v-if="settings.dots && !settings.unagile"
            ref="dots"
            class="agile__dots"
        >
            <li
                v-for="n in countSlides"
                :key="n"
                :class="{'agile__dot--current': n - 1 === currentSlide}"
                class="agile__dot"
                @mouseout="handleMouseOut('dot')"
                @mouseover="handleMouseOver('dot')"
            >
                <button
                    type="button"
                    @click="goTo(n - 1), restartAutoPlay()"
                >
                    {{ n }}
                </button>
            </li>
        </ul>

        <button
            v-if="settings.navButtons && !settings.unagile"
            ref="nextButton"
            :disabled="!canGoToNext"
            aria-label="Next"
            class="agile__nav-button agile__nav-button--next"
            type="button"
            @click="goToNext(), restartAutoPlay()"
        >
            <slot name="nextbutton">
                →
            </slot>
        </button>

    </div>
</template>

<script lang="ts">
// Rewrite of vue-agile v2.0 in typescript
import {defineComponent, PropType, Slot} from 'vue';
import throttle from 'lodash.throttle';


type SliderSettings = {
    asNavFor: any[],
    autoplay: boolean,
    autoplaySpeed: number,
    centerMode: boolean,
    centerPadding: string,
    changeDelay: number,
    dots: boolean,
    fade: boolean,
    infinite: boolean,
    initialSlide: number,
    mobileFirst: boolean,
    navButtons: boolean,
    pauseOnDotsHover: boolean,
    pauseOnHover: boolean,
    rtl: boolean,
    slidesToScroll: number,
    slidesToShow: number,
    speed: number,
    swipeDistance: number,
    throttleDelay: number,
    timing: string,
    unagile: boolean,
};
type ResponsiveSetting = {
    breakpoint: number,
    settings: SliderSettings,
}
type SettingsKeys = keyof SliderSettings;
type InitialSliderSettings = SliderSettings & {responsive: ResponsiveSetting[]}

export default defineComponent({
    name: 'agile',

    emits: ['before-change', 'after-change', 'breakpoint'],

    props: {
    /**
         * Set the carousel to be the navigation of other carousels
         */
        asNavFor: {
            type: Array,
            default: () => [],
        },

        /**
         * Enable autoplay
         */
        autoplay: {
            type: Boolean,
            default: false,
        },

        /**
         * Autoplay interval in milliseconds
         */
        autoplaySpeed: {
            type: Number,
            default: 3000,
        },

        /**
         * Enable centered view when slidesToShow > 1
         */
        centerMode: {
            type: Boolean,
            default: false,
        },

        /**
         * Slides padding in center mode
         */
        centerPadding: {
            type: String,
            default: '15%',
        },

        /**
         * Slide change delay in milliseconds
         */
        changeDelay: {
            type: Number,
            default: 0,
        },

        /**
         * Enable dot indicators/pagination
         */
        dots: {
            type: Boolean,
            default: true,
        },

        /**
         * Enable fade effect
         */
        fade: {
            type: Boolean,
            default: false,
        },

        /**
         * Infinite loop sliding
         */
        infinite: {
            type: Boolean,
            default: true,
        },

        /**
         * Index of slide to start on
         */
        initialSlide: {
            type: Number,
            default: 0,
        },

        /**
         * Enable mobile first calculation for responsive settings
         */
        mobileFirst: {
            type: Boolean,
            default: true,
        },

        /**
         * Enable prev/next navigation buttons
         */
        navButtons: {
            type: Boolean,
            default: true,
        },

        /**
         * All settings as one object
         */
        options: {
            type: Object,
            default: () => {},
        },

        /**
         * Pause autoplay when a dot is hovered
         */
        pauseOnDotsHover: {
            type: Boolean,
            default: false,
        },

        /**
         * Pause autoplay when a slide is hovered
         */
        pauseOnHover: {
            type: Boolean,
            default: true,
        },

        /**
         * Object containing breakpoints and settings objects
         */
        responsive: {
            type: Array as PropType<ResponsiveSetting[]>,
            default: () => [],
        },

        /**
         * Enable right-to-left mode
         */
        rtl: {
            type: Boolean,
            default: false,
        },

        /**
         * Number of slides to scroll
         */
        slidesToScroll: {
            type: Number,
            default: 1,
        },

        /**
         * Number of slides to show
         */
        slidesToShow: {
            type: Number,
            default: 1,
        },

        /**
         * Slide animation speed in milliseconds
         */
        speed: {
            type: Number,
            default: 300,
        },

        /**
         * Swipe distance
         */
        swipeDistance: {
            type: Number,
            default: 50,
        },

        /**
         * Throttle delay in milliseconds
         */
        throttleDelay: {
            type: Number,
            default: 500,
        },

        /**
         * Transition timing function
         * Available: ease, linear, ease-in, ease-out, ease-in-out
         */
        timing: {
            type: String,
            default: 'ease',
            // validator: (value: string) => {
            //     return ['ease', 'linear', 'ease-in', 'ease-out', 'ease-in-out'].indexOf(value) !== -1;
            // },
        },

        /**
         * Disable Agile carousel
         */
        unagile: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            autoplayInterval: undefined as any,
            autoplayRemaining: null,
            autoplayStartTimestamp: null as null|number,
            autoplayTimeout: undefined as any,
            currentSlide: 0,
            dragDistance: 0,
            dragStartX: 0,
            dragStartY: 0,
            isAutoplayPaused: false,
            isMouseDown: false,
            slides: [] as HTMLElement[],
            slidesClonedAfter: [] as HTMLElement[],
            slidesClonedBefore: [] as HTMLElement[],
            isSSR: (typeof window === 'undefined'),
            transitionDelay: 0,
            translateX: 0,
            widthWindow: 0,
            widthContainer: 0,
            remaining: 0,
        };
    },

    computed: {
    // Initial settings based on props and options object
        initialSettings(): InitialSliderSettings {
            // options prop is excluded
            // @ts-ignore
            let {options, ...initialSettings} = this.$props;

            // Join settings from options
            if (options) {
                initialSettings = {...initialSettings, ...options};
            }

            // // Sort breakpoints
            // if (initialSettings.responsive) {
            //     initialSettings.responsive = orderBy(initialSettings.responsive, 'breakpoint');
            // }

            return initialSettings;
        },

        // Settings for current breakpoint
        settings(): SliderSettings {
            const {responsive, ...settings} = this.initialSettings;
            let settingsWithResponsive: SliderSettings = Object.assign({}, settings);

            if (responsive) {
                responsive.sort((itemA, itemB) => {
                    return itemA.breakpoint - itemB.breakpoint;
                }).forEach((option: ResponsiveSetting) => {
                    if (settings.mobileFirst ? option.breakpoint < this.widthWindow : option.breakpoint > this.widthWindow) {
                        settingsWithResponsive = Object.assign(settingsWithResponsive, option.settings);
                    }
                });
            }

            return settingsWithResponsive;
        },
        breakpoints(): number[] {
            return (!this.initialSettings.responsive) ? [] : this.initialSettings.responsive.map((item: any) => item.breakpoint).sort();
        },

        canGoToPrev(): boolean {
            return (this.settings.infinite || this.currentSlide > 0);
        },

        canGoToNext(): boolean {
            return (this.settings.infinite || this.currentSlide < this.countSlides - 1);
        },

        countSlides(): number {
            return (this.isSSR) ? this.htmlCollectionToArray(this.$slots.default).length : this.slides.length;
        },

        countSlidesAll(): number {
            return this.slidesAll.length;
        },

        currentBreakpoint(): number|null {
            const breakpoints = this.breakpoints.map((item: any) => item).reverse();
            return (this.initialSettings.mobileFirst) ? breakpoints.find((item: any) => item < this.widthWindow) || 0 : breakpoints.find((item: any) => item > this.widthWindow) || null;
        },

        marginX(): number {
            if (this.settings.unagile) {
                return 0;
            }

            let marginX = (this.slidesCloned) ? this.countSlides * this.widthSlide : 0;

            // Center mode margin
            if (this.settings.centerMode) {
                marginX -= (Math.floor(this.settings.slidesToShow / 2) - +(this.settings.slidesToShow % 2 === 0)) * this.widthSlide;
            }

            return (this.settings.rtl) ? marginX : -1 * marginX;
        },

        slidesCloned(): boolean {
            return (!this.settings.unagile && !this.settings.fade && this.settings.infinite);
        },

        slidesAll(): HTMLElement[] {
            return (this.slidesCloned) ? [...this.slidesClonedBefore, ...this.slides, ...this.slidesClonedAfter] : this.slides;
        },

        widthSlide(): number {
            return this.widthContainer / this.settings.slidesToShow;
        },
    },

    created() {
        this.goTo = throttle(this.goTo, this.throttleDelay);
        this.getWidth = throttle(this.getWidth, 500);
    },
    mounted() {
    // Windows resize listener
        window.addEventListener('resize', this.getWidth);

        // Mouse and touch events
        const track = this.$refs.track as HTMLElement;
        track.addEventListener('touchstart', this.handleMouseDown);
        track.addEventListener('touchend', this.handleMouseUp);
        track.addEventListener('touchmove', this.handleMouseMove);
        track.addEventListener('mousedown', this.handleMouseDown);
        track.addEventListener('mouseup', this.handleMouseUp);
        track.addEventListener('mousemove', this.handleMouseMove);

        // Init
        this.isSSR = false;
        this.reload();
    },

    // Vue 3
    beforeUnmount() {
        this.destroy();
    },

    methods: {
        prepareSlides() {
            this.slides = this.htmlCollectionToArray((this.$refs.slides as HTMLElement).children);

            // Probably timeout needed
            if (this.slidesCloned) {
                this.slidesClonedBefore = this.htmlCollectionToArray((this.$refs.slidesClonedBefore as HTMLElement).children);
                this.slidesClonedAfter = this.htmlCollectionToArray((this.$refs.slidesClonedAfter as HTMLElement).children);
            }

            for (const slide of this.slidesAll) {
                slide.classList.add('agile__slide');
            }
        },
        prepareSlidesClasses() {
            if (this.currentSlide === null) {
                return false;
            }

            // Remove active & current classes
            for (let i = 0; i < this.countSlides; i++) {
                this.slides[i].classList.remove('agile__slide--active');
                this.slides[i].classList.remove('agile__slide--current');
            }

            // Add active & current class for current slide
            setTimeout(() => this.slides[this.currentSlide].classList.add('agile__slide--active'), this.changeDelay);

            let start = (this.slidesCloned) ? this.countSlides + this.currentSlide : this.currentSlide;

            if (this.centerMode) {
                start -= (Math.floor(this.settings.slidesToShow / 2) - +(this.settings.slidesToShow % 2 === 0));
            }

            // To account for the combination of infinite = false and centerMode = true, ensure we don't overrun the bounds of the slide count.
            for (let i = Math.max(start, 0); i < Math.min(start + this.settings.slidesToShow, this.countSlides); i++) {
                this.slidesAll[i].classList.add('agile__slide--current');
            }
        },

        /**
         * Prepare carousel styles
         */
        prepareCarousel() {
            // Prepare track
            if (this.settings.unagile) {
                this.translateX = 0;
            } else {
                if (this.currentSlide === null && this.countSlides) {
                    this.currentSlide = this.settings.initialSlide;
                }

                if (this.currentSlide > this.countSlides) {
                    this.currentSlide = this.countSlides - 1;
                }

                this.goTo(this.currentSlide, false, false);
            }
        },
        getWidth() {
            if (this.isSSR) {
                return false;
            }

            this.widthWindow = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
            this.widthContainer = (this.$refs.list as HTMLElement).clientWidth;
        },
        htmlCollectionToArray(collection: HTMLCollection|Slot|undefined): HTMLElement[] {
            return Array.prototype.slice.call(collection, 0);
        },
        destroy() {
            window.removeEventListener('resize', this.getWidth);

            const track = this.$refs.track as HTMLElement;
            track.removeEventListener('touchstart', this.handleMouseDown);
            track.removeEventListener('touchend', this.handleMouseUp);
            track.removeEventListener('touchmove', this.handleMouseMove);
            track.removeEventListener('mousedown', this.handleMouseDown);
            track.removeEventListener('mouseup', this.handleMouseUp);
            track.removeEventListener('mousemove', this.handleMouseMove);

            this.disableAutoPlay();
        },

        // Return current breakpoint
        getCurrentBreakpoint() {
            return this.currentBreakpoint;
        },

        // Return settings for current breakpoint
        getCurrentSettings() {
            return this.settings;
        },

        // Return current slide index
        getCurrentSlide() {
            return this.currentSlide;
        },

        // Return initial settings
        getInitialSettings() {
            return this.initialSettings;
        },

        // Go to slide
        goTo(n: number, transition = true, asNav = false) {
            // Break goTo() if unagile is active
            if (this.settings.unagile) {
                return false;
            }

            if (!asNav) {
                this.settings.asNavFor.forEach((carousel: any) => {
                    if (carousel) {
                        carousel.goTo(n, transition, true);
                    }
                });
            }

            let slideNextReal = n;

            if (transition) {
                if (this.settings.infinite && n < 0) {
                    slideNextReal = this.countSlides - 1;
                } else if (n >= this.countSlides) {
                    slideNextReal = 0;
                }

                this.$emit('before-change', {currentSlide: this.currentSlide, nextSlide: slideNextReal});

                this.currentSlide = slideNextReal;

                if (n !== slideNextReal) {
                    setTimeout(() => {
                        this.goTo(slideNextReal, false);
                    }, this.settings.speed);
                }
            }

            const translateX = (!this.settings.fade) ? n * this.widthSlide * this.settings.slidesToScroll : 0;
            this.transitionDelay = (transition) ? this.speed : 0;

            if (this.infinite || (this.currentSlide + this.slidesToShow <= this.countSlides)) {
                this.translateX = (this.settings.rtl) ? translateX : -1 * translateX;
            }
        },

        // Go to next slide
        goToNext() {
            if (this.canGoToNext) {
                this.goTo(this.currentSlide + 1);
            }
        },

        // Go to previous slide
        goToPrev() {
            if (this.canGoToPrev) {
                this.goTo(this.currentSlide - 1);
            }
        },

        // Reload carousel
        reload() {
            this.getWidth();
            this.prepareSlides();
            this.prepareCarousel();
            this.toggleFade();
            this.toggleAutoPlay();
        },
        clearAutoPlayPause() {
            clearTimeout(this.autoplayTimeout);
            this.autoplayRemaining = null;
        },

        disableAutoPlay() {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        },

        disableScroll() {
            document.ontouchmove = (e) => e.preventDefault();
        },

        enableScroll() {
            document.ontouchmove = () => true;
        },

        restartAutoPlay() {
            this.disableAutoPlay();
            this.toggleAutoPlay();
        },

        toggleAutoPlay() {
            const enabled = (!this.settings.unagile && this.settings.autoplay);

            if (!this.autoplayInterval && enabled) {
                this.autoplayInterval = setInterval(() => {
                    if (!document.hidden) {
                        if (!this.canGoToNext) {
                            this.disableAutoPlay();
                        } else {
                            this.goToNext();
                        }
                    }
                }, this.settings.autoplaySpeed);
            } else {
                this.disableAutoPlay();
            }
        },

        toggleFade() {
            const enabled = (!this.settings.unagile && this.settings.fade);

            for (let i = 0; i < this.countSlides; i++) {
                this.slides[i].style.transition = (enabled) ? 'opacity ' + this.settings.timing + ' ' + this.settings.speed + 'ms' : 'none';
                this.slides[i].style.transform = (enabled) ? `translate(-${i * this.widthSlide}px)` : 'none';
            }
        },
        handleMouseDown(e: MouseEvent|TouchEvent) {
            this.isMouseDown = true;

            if (window.TouchEvent && e instanceof TouchEvent) {
                this.dragStartX = e.touches[0].clientX;
                this.dragStartY = e.touches[0].clientY;
            }
            if (e instanceof MouseEvent) {
                this.dragStartX = e.clientX;
                this.dragStartY = e.clientY;
            }
        },

        handleMouseMove(e: MouseEvent|TouchEvent) {
            let positionX = 0;
            let positionY = 0;
            if (window.TouchEvent && e instanceof TouchEvent) {
                positionX = e.touches[0].clientX;
                positionY = e.touches[0].clientY;
            }
            if (e instanceof MouseEvent) {
                positionX = e.clientX;
                positionY = e.clientY;
            }
            const dragDistanceX = Math.abs(positionX - this.dragStartX);
            const dragDistanceY = Math.abs(positionY - this.dragStartY);
            if (dragDistanceX > 3 * dragDistanceY) {
                this.disableScroll();
                this.dragDistance = positionX - this.dragStartX;
            }
        },

        handleMouseUp() {
            this.isMouseDown = false;
            this.enableScroll();
        },

        handleMouseOver(element: string) {
            if (this.settings.autoplay) {
                if ((element === 'dot' && this.settings.pauseOnDotsHover) || (element === 'track' && this.settings.pauseOnHover)) {
                    this.isAutoplayPaused = true;
                }
            }
        },

        handleMouseOut(element: string) {
            if (this.settings.autoplay) {
                if ((element === 'dot' && this.settings.pauseOnDotsHover) || (element === 'track' && this.settings.pauseOnHover)) {
                    this.isAutoplayPaused = false;
                }
            }
        },
    },
    watch: {
    // Recalculate settings
        currentBreakpoint() {
            // this.prepareSettings()
            this.$emit('breakpoint', {breakpoint: this.currentBreakpoint});
        },

        // Watch current slide change
        currentSlide() {
            this.prepareSlidesClasses();

            // Set start time of slide
            this.autoplayStartTimestamp = (this.settings.autoplay) ? +new Date() : null;

            this.$emit('after-change', {currentSlide: this.currentSlide});
        },

        // Watch drag distance change
        dragDistance() {
            if (this.isMouseDown) {
                const {rtl} = this.settings;
                const dragDistance = this.dragDistance * (rtl ? -1 : 1);

                if (dragDistance > this.swipeDistance && this.canGoToPrev) {
                    this.goToPrev();
                    this.handleMouseUp();
                }

                if (dragDistance < -1 * this.swipeDistance && this.canGoToNext) {
                    this.goToNext();
                    this.handleMouseUp();
                }
            }
        },

        isAutoplayPaused(nevValue) {
            if (nevValue && this.autoplayStartTimestamp) {
                // Store current slide remaining time and disable auto play mode
                this.remaining = this.settings.autoplaySpeed - (+new Date() - this.autoplayStartTimestamp);
                this.disableAutoPlay();
                this.clearAutoPlayPause();
            } else {
                // Go to next after remaining time and rerun auto play mode
                this.autoplayTimeout = setTimeout(() => {
                    this.clearAutoPlayPause();
                    this.goToNext();
                    this.toggleAutoPlay();
                }, this.remaining);
            }
        },

        'settings.autoplay'() {
            this.toggleAutoPlay();
        },

        'settings.fade'() {
            this.toggleFade();
        },

        'settings.unagile'() {
            // this.prepareSlides()
            // this.prepareCarousel()
        },

        widthSlide() {
            for (let i = 0; i < this.countSlidesAll; i++) {
                // console.log(this.widthSlide)
                // console.log(this.settings)
                if (!this.settings.unagile) {
                    this.slidesAll[i].style.width = `${this.widthSlide}px`;
                } else {
                    this.slidesAll[i].style.width = `auto`;
                }
            }
        },

        // Watch window width change
        widthWindow(newValue, oldValue) {
            if (oldValue) {
                this.prepareCarousel();
                this.toggleFade();
            }
        },
    },
});
</script>
